import { decompress as zstdDecompress } from 'fzstd';
// libraw-wasm is loaded at runtime through ./librawLoader, never through the
// bundler: a top-level import made rollup deadlock at production-build time (the
// commonjs resolver + the worker-import-meta-url plugin never settle on libraw's
// `new Worker(new URL(...))`). Loading it lazily also keeps the ~1.2MB wasm out
// of the main bundle until a DNG actually needs decoding.
import { loadLibRaw } from './librawLoader';

/**
 * SKIN-43 — turn a *decrypted* clinical-image blob into something a browser
 * <img> can render.
 *
 * The capture pipeline (SKIN-45) writes a zstd-compressed DNG, so on read we
 * mirror it in reverse: decrypt (done by the caller) → unzstd → DNG-decode to
 * RGB → wrap as a PNG blob. Legacy files (plain JPEG, no compression) skip both
 * steps and are wrapped as-is, so nothing about pre-SKIN-43 images changes.
 *
 * The codec is declared per file by the server (EncryptedFile.format /
 * .compression); the server is zero-knowledge and never inspects the bytes.
 */
export interface ClinicalImageCodec {
    /**
     * Declared container format ('jpeg' | 'png' | 'dng'), used only as a
     * fallback when the magic bytes are inconclusive. Compression is detected
     * purely from the bytes (the zstd frame is unambiguous), so it is not part
     * of this hint — the bytes are authoritative.
     */
    format?: string | null;
}

function startsWith(bytes: Uint8Array, sig: number[]): boolean {
    if (bytes.length < sig.length) return false;
    for (let i = 0; i < sig.length; i++) if (bytes[i] !== sig[i]) return false;
    return true;
}

// Container magic numbers. These are unambiguous, so we trust the bytes over the
// declared codec: it both fixes files whose stored metadata is wrong (e.g.
// zstd-DNG captured by SKIN-45 before the server persisted format/compression,
// which default to jpeg/none) and keeps working if metadata is ever absent.
const isZstd = (b: Uint8Array) => startsWith(b, [0x28, 0xb5, 0x2f, 0xfd]);
const isTiff = (b: Uint8Array) => startsWith(b, [0x49, 0x49, 0x2a, 0x00]) || startsWith(b, [0x4d, 0x4d, 0x00, 0x2a]); // DNG is TIFF/EP
const isJpeg = (b: Uint8Array) => startsWith(b, [0xff, 0xd8]);
const isPng = (b: Uint8Array) => startsWith(b, [0x89, 0x50, 0x4e, 0x47]);

export async function decodeClinicalImageToBlob(
    bytes: Uint8Array,
    codec: ClinicalImageCodec,
): Promise<Blob> {
    let data = bytes;

    // Compression — byte-authoritative. A zstd frame is unambiguous; sniffing it
    // also rescues blobs stored as compression:'none' that are really zstd.
    if (isZstd(data)) {
        data = zstdDecompress(data);
    }

    // Format — sniff the (decompressed) bytes first, in magic-number order, and
    // fall back to the declared format only once the magic is exhausted. The
    // order is the whole point: it is what makes the declared format a
    // tie-breaker rather than an override.
    if (isTiff(data)) return dngToPngBlob(data);
    if (isJpeg(data)) return new Blob([data], { type: 'image/jpeg' });
    if (isPng(data)) return new Blob([data], { type: 'image/png' });

    // No magic matched. A declared 'dng' is still worth trying, since a RAW
    // container we do not recognise is exactly what libraw exists to open.
    if (codec.format === 'dng') return dngToPngBlob(data);

    // SKIN-206 — bytes we cannot identify are a decode failure, not a JPEG.
    // This used to fall through to `image/jpeg`, which handed the caller a Blob
    // it turned into an object URL, so a corrupt or truncated clinical image
    // reached the clinician as a broken <img> with nothing saying so. A declared
    // format is deliberately not enough to override the bytes here: trusting it
    // is what produced the silent wrap in the first place.
    throw new Error(
        `Unrecognised clinical image: ${data.length} byte(s) matching no known container `
        + `(zstd / DNG / JPEG / PNG)${codec.format ? `, declared format '${codec.format}'` : ''}`,
    );
}

/**
 * Decode a DNG (RAW) into a PNG blob via libraw-wasm. The lib runs the heavy
 * decode in its own Web Worker; we keep the worker short-lived (one per image)
 * and always dispose it. `halfSize` keeps the on-screen render fast and memory
 * bounded for ~25-50MP captures; `useCameraWb` honours the camera white balance
 * so colours match what the clinician saw.
 */
async function dngToPngBlob(dng: Uint8Array): Promise<Blob> {
    const LibRaw = await loadLibRaw();
    const lr = new LibRaw();
    try {
        await lr.open(dng, { halfSize: true, useCameraWb: true, outputBps: 8 });
        const img = await lr.imageData();
        if (!img || !img.data || !img.width || !img.height) {
            throw new Error('DNG decode produced no image data');
        }
        return rgbToPngBlob(img.data, img.width, img.height, img.colors, img.bits);
    } finally {
        lr.dispose();
    }
}

/** Paint decoded RGB(A) pixels onto a canvas and export as a PNG blob. */
async function rgbToPngBlob(
    pixels: Uint8Array | Uint16Array,
    width: number,
    height: number,
    channels: number,
    bits: number,
): Promise<Blob> {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable');

    // We request 8-bit output (outputBps:8). Guard the 16-bit case anyway: a
    // raw 16-bit sample (0..65535) written into the 8-bit canvas would clamp to
    // 0/255 and render as garbage, so scale it down instead of corrupting.
    const shift = bits > 8 ? 8 : 0;
    const out = ctx.createImageData(width, height);
    const ch = channels || pixels.length / (width * height);
    for (let i = 0, src = 0; i < width * height; i++, src += ch) {
        const d = i * 4;
        out.data[d] = pixels[src] >> shift;
        out.data[d + 1] = ch > 1 ? pixels[src + 1] >> shift : pixels[src] >> shift;
        out.data[d + 2] = ch > 2 ? pixels[src + 2] >> shift : pixels[src] >> shift;
        out.data[d + 3] = 255;
    }
    ctx.putImageData(out, 0, 0);

    return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null'))),
            'image/png',
        );
    });
}
